/**
 * One invoice: what it says, whether the customer has it, and whether Xero does.
 *
 * The three states are shown in the order they happen, because each depends on
 * the one before (D50): raised → in the customer's hands → in Xero. An invoice
 * that is raised but not sent is not owed and is not in the accounts, and this
 * screen says so rather than letting "issued" imply otherwise.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, api, openDocument } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { day, money, qty, time, titleCase } from "../lib/format";
import type { InvoiceDetail as Invoice, PaymentIn } from "../lib/types";
import { Card, Chip, ErrorState, Loading, PageHeader } from "../components/ui";

const HOW = {
  email: "Emailed",
  by_hand: "Handed over",
  portal: "Uploaded to their portal",
  other: "Sent another way",
} as const;

export default function InvoiceDetail() {
  const { id = "" } = useParams();
  const { can } = useAuth();
  const isOffice = can(...OFFICE);
  const canDelete = can("owner", "office");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: () => api.invoices.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["contracts"] });
      navigate("/invoices?show=unsent");
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't delete it."),
  });

  const invoice = useQuery({ queryKey: ["invoice", id], queryFn: () => api.invoices.get(id) });
  const refresh = (next?: Invoice) => {
    if (next) queryClient.setQueryData(["invoice", id], next);
    void queryClient.invalidateQueries({ queryKey: ["invoices"] });
  };

  if (invoice.isPending) return <div className="page"><Loading what="Loading the invoice" /></div>;
  if (invoice.error) {
    return (
      <div className="page">
        <ErrorState error={invoice.error} onRetry={() => void invoice.refetch()} />
      </div>
    );
  }
  const inv = invoice.data;

  return (
    <div className="page">
      <PageHeader
        eyebrow={
          inv.kind === "final"
            ? "Invoice"
            : inv.kind === "contract"
              ? "Agreement invoice"
              : inv.kind === "credit"
                ? "Credit note (adjustment note)"
                : `${titleCase(inv.kind)} invoice`
        }
        title={`${inv.number} · ${inv.customer_name}`}
        actions={
          <div className="btn-row">
            <button
              className="btn"
              type="button"
              onClick={() =>
                void openDocument(`/invoices/${inv.id}/pdf`).catch((err) =>
                  setProblem(err instanceof ApiError ? err.message : "Couldn't open the PDF."),
                )
              }
            >
              Open the PDF
            </button>
            {/* Only before it is sent: until then it is not a debt and Xero has
                not heard of it. After, a correction is a credit note (D51). */}
            {canDelete && !inv.sent_at && inv.paid_cents === 0 && (
              <button
                className="btn"
                type="button"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Delete ${inv.number}? It hasn't been sent, so nobody else has a copy.`)) {
                    remove.mutate();
                  }
                }}
              >
                Delete
              </button>
            )}
          </div>
        }
      >
        {inv.reference ?? "Not raised from a job"}
        {inv.credits_invoice_id && (
          <>
            {" · "}
            <Link to={`/invoices/${inv.credits_invoice_id}`}>open {inv.credits_invoice_number}</Link>
          </>
        )}
        {inv.kind === "contract" && (
          <>
            {" · "}
            <Link to="/contracts">open the agreements</Link>
          </>
        )}
        {inv.job_id && (
          <>
            {" · "}
            <Link to={`/jobs/${inv.job_id}`}>open the job</Link>
          </>
        )}
      </PageHeader>

      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}

      <div className="split">
        <div className="split-main">
          <Card title="Lines" subtitle="Amounts exclude GST" padded={false}>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Qty</th>
                    <th className="right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.lines.map((line) => (
                    <tr key={line.id}>
                      <td>{line.description}</td>
                      <td className="mono">{qty(line.qty)}</td>
                      <td className={`right mono${line.unit_sell_cents < 0 ? " text-ok" : ""}`}>
                        {money(Math.round(line.unit_sell_cents * line.qty))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dl className="kv kv-money" style={{ padding: "12px 14px" }}>
              <dt>Subtotal</dt>
              <dd className="mono">{money(inv.subtotal_cents)}</dd>
              <dt>GST</dt>
              <dd className="mono">{money(inv.tax_cents)}</dd>
              <dt className="total">{inv.kind === "credit" ? "Total credit" : "Total"}</dt>
              <dd className="mono total">{money(inv.total_cents)}</dd>
              {inv.paid_cents > 0 && (
                <>
                  <dt>Paid</dt>
                  <dd className="mono">−{money(inv.paid_cents)}</dd>
                </>
              )}
              {inv.credited_cents > 0 && (
                <>
                  <dt>Credited</dt>
                  <dd className="mono">−{money(inv.credited_cents)}</dd>
                </>
              )}
              {(inv.paid_cents > 0 || inv.credited_cents > 0) && (
                <>
                  <dt>Still owed</dt>
                  <dd className="mono">{money(inv.outstanding_cents)}</dd>
                </>
              )}
            </dl>
          </Card>

          {inv.kind !== "credit" && inv.sent_at && (
            <PaymentsCard inv={inv} canRecord={canDelete} onChanged={(next) => refresh(next)} />
          )}

          {inv.kind !== "credit" && (inv.credit_notes.length > 0 || (canDelete && inv.sent_at && inv.creditable_cents > 0)) && (
            <CreditCard inv={inv} canCredit={canDelete} />
          )}
        </div>

        <aside className="split-side">
          <Card title="Dates">
            <dl className="kv">
              <dt>Issued</dt>
              <dd>{inv.issued_on ? day(inv.issued_on) : "—"}</dd>
              <dt>Due</dt>
              <dd className={inv.overdue ? "text-crit" : undefined}>
                {inv.due_on ? day(inv.due_on) : "—"}
                {inv.overdue && " · overdue"}
              </dd>
            </dl>
          </Card>

          <CustomerCard inv={inv} isOffice={isOffice} onChanged={refresh} />
          <XeroCard inv={inv} isOffice={isOffice} onChanged={() => void invoice.refetch()} />
        </aside>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- the customer */

function CustomerCard({
  inv,
  isOffice,
  onChanged,
}: {
  inv: Invoice;
  isOffice: boolean;
  onChanged: (next?: Invoice) => void;
}) {
  const [to, setTo] = useState(inv.customer_email ?? "");
  const [note, setNote] = useState("");
  const [said, setSaid] = useState<{ tone: "info" | "crit"; text: string } | null>(null);
  const [manual, setManual] = useState(false);

  const send = useMutation({
    mutationFn: () => api.invoices.send(inv.id, { to: to.trim(), note: note.trim() || undefined }),
    onSuccess: (result) => {
      setSaid(
        result.delivered
          ? { tone: "info", text: `Sent to ${to.trim()} with the PDF attached.` }
          : // Said plainly, because the invoice now stays "not sent" and the
            // office needs to know why before they tell the customer it went.
            { tone: "crit", text: `Not sent: ${result.detail}` },
      );
      onChanged(result.invoice);
    },
    onError: (err) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't send it." }),
  });

  const mark = useMutation({
    mutationFn: (how: "by_hand" | "portal" | "other") => api.invoices.markSent(inv.id, { how }),
    onSuccess: (next) => {
      setManual(false);
      setSaid({ tone: "info", text: "Recorded — the customer has it, and it goes to Xero next." });
      onChanged(next);
    },
    onError: (err) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't record it." }),
  });

  return (
    <Card title="The customer">
      {inv.sent_at ? (
        <p>
          <Chip tone="ok">{HOW[(inv.sent_how ?? "other") as keyof typeof HOW] ?? "Sent"}</Chip>{" "}
          <span className="tiny muted">{day(inv.sent_at)} {time(inv.sent_at)}</span>
        </p>
      ) : (
        <p className="notice notice-warn">
          <b>Not sent yet.</b>{" "}
          {inv.kind === "credit"
            ? `It doesn't reduce ${inv.credits_invoice_number ?? "the invoice"}, and it won't go to Xero, until the customer has it.`
            : "It isn't owed, and it won't go to Xero, until the customer has it."}
        </p>
      )}

      {said && (
        <p className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"}>
          {said.text}
        </p>
      )}

      {isOffice && (
        <>
          <label className="field" style={{ marginTop: 10 }}>
            <span>{inv.sent_at ? "Send a copy to" : "Email it to"}</span>
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="accounts@customer.com.au" />
          </label>
          <label className="field" style={{ marginTop: 8 }}>
            <span>Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Thanks for having us on site." />
          </label>
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button
              className="btn btn-primary"
              type="button"
              disabled={send.isPending || !to.trim()}
              onClick={() => send.mutate()}
            >
              {send.isPending ? "Sending…" : "Send with the PDF"}
            </button>
            {!inv.sent_at && (
              <button className="btn" type="button" onClick={() => setManual((m) => !m)}>
                It went another way
              </button>
            )}
          </div>
          {manual && !inv.sent_at && (
            <div className="btn-row" style={{ marginTop: 8 }}>
              <button className="btn" type="button" disabled={mark.isPending} onClick={() => mark.mutate("by_hand")}>
                Handed over
              </button>
              <button className="btn" type="button" disabled={mark.isPending} onClick={() => mark.mutate("portal")}>
                Their supplier portal
              </button>
              <button className="btn" type="button" disabled={mark.isPending} onClick={() => mark.mutate("other")}>
                Other
              </button>
            </div>
          )}
        </>
      )}

      {inv.deliveries.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary className="tiny muted">History ({inv.deliveries.length})</summary>
          <ul className="tiny" style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {inv.deliveries.map((d, i) => (
              <li key={i}>
                {day(d.attempted_at)} {time(d.attempted_at)} — {HOW[d.channel as keyof typeof HOW] ?? d.channel}
                {d.to_address ? ` to ${d.to_address}` : ""}:{" "}
                <span className={d.status === "sent" ? "text-ok" : "text-crit"}>{d.status}</span>
                {d.status !== "sent" && d.detail ? ` (${d.detail})` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

/* ---------------------------------------------------------------------- Xero */

function XeroCard({ inv, isOffice, onChanged }: { inv: Invoice; isOffice: boolean; onChanged: () => void }) {
  const [problem, setProblem] = useState<string | null>(null);
  const push = useMutation({
    mutationFn: () => api.accounting.push(),
    onSuccess: onChanged,
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't push."),
  });
  const a = inv.accounts;

  let body: React.ReactNode;
  if (!inv.sent_at) {
    body = <p className="tiny muted">Goes to Xero once the customer has it.</p>;
  } else if (!a) {
    body = <p className="tiny muted">Queued.</p>;
  } else if (a.status === "pushed") {
    body = (
      <p>
        <Chip tone="ok">In Xero</Chip>{" "}
        <span className="tiny muted">{a.pushed_at ? `${day(a.pushed_at)} ${time(a.pushed_at)}` : ""}</span>
      </p>
    );
  } else {
    const tone = a.status === "failed" || a.status === "mismatch" ? "crit" : "warn";
    const label = {
      pending: "Waiting",
      retrying: "Retrying",
      failed: "Needs a look",
      mismatch: "Totals differ",
    }[a.status];
    body = (
      <>
        <p>
          <Chip tone={tone}>{label}</Chip>
          {a.attempts > 0 && <span className="tiny muted"> · {a.attempts} attempt{a.attempts === 1 ? "" : "s"}</span>}
        </p>
        {a.last_error && (
          <p className={`tiny ${tone === "crit" ? "text-crit" : "muted"}`} style={{ marginTop: 6 }}>
            {a.last_error}
          </p>
        )}
        {isOffice && a.status !== "mismatch" && (
          <div className="btn-row" style={{ marginTop: 8 }}>
            <button className="btn" type="button" disabled={push.isPending} onClick={() => push.mutate()}>
              {push.isPending ? "Pushing…" : "Push now"}
            </button>
            <Link className="btn" to="/settings#xero">
              Xero settings
            </Link>
          </div>
        )}
      </>
    );
  }

  return (
    <Card title="Xero">
      {problem && (
        <p className="tiny text-crit" role="alert">
          {problem}
        </p>
      )}
      {body}
    </Card>
  );
}

/* ------------------------------------------------------------- credit notes */

/**
 * Correcting an invoice the customer already has (BILL-8). A sent invoice
 * cannot change — the customer and Xero both hold a copy — so a mistake is a
 * credit note that names it: all of it, or a particular amount.
 */
function CreditCard({ inv, canCredit }: { inv: Invoice; canCredit: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [whole, setWhole] = useState(true);
  const [description, setDescription] = useState("");
  const [dollars, setDollars] = useState("");
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const cents = Math.round(Number(dollars.replace(/[$,\s]/g, "")) * 100);
  const credit = useMutation({
    mutationFn: () =>
      api.invoices.credit(
        inv.id,
        whole
          ? { whole: true, reason: reason.trim() || undefined }
          : { lines: [{ description: description.trim(), amount_cents: cents }], reason: reason.trim() || undefined },
      ),
    onSuccess: (note) => {
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["invoice", inv.id] });
      navigate(`/invoices/${note.id}`);
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't raise the credit note."),
  });

  const wholeAllowed = inv.creditable_cents === inv.total_cents;

  return (
    <Card title="Credit notes" subtitle={inv.creditable_cents > 0 ? `Up to ${money(inv.creditable_cents)} can still be credited` : "Fully credited"}>
      {inv.credit_notes.length > 0 && (
        <div className="stack" style={{ marginBottom: 10 }}>
          {inv.credit_notes.map((c) => (
            <Link key={c.id} to={`/invoices/${c.id}`} className="tiny">
              <b className="mono">{c.number}</b> · {money(c.total_cents)} · {c.sent_at ? `sent ${day(c.sent_at)}` : "not sent yet"}
            </Link>
          ))}
        </div>
      )}
      {canCredit && inv.sent_at && inv.creditable_cents > 0 && !open && (
        <button className="btn" type="button" onClick={() => { setOpen(true); setWhole(wholeAllowed); }}>
          Raise a credit note
        </button>
      )}
      {open && (
        <div className="stack">
          <div className="segmented" role="group" aria-label="What to credit">
            <button type="button" className={whole ? "active" : ""} disabled={!wholeAllowed} onClick={() => setWhole(true)}>
              The whole invoice
            </button>
            <button type="button" className={!whole ? "active" : ""} onClick={() => setWhole(false)}>
              An amount
            </button>
          </div>
          {!whole && (
            <>
              <label className="field">
                <span>What it's for</span>
                <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Reader not supplied" />
              </label>
              <label className="field">
                <span>Amount ex GST</span>
                <input inputMode="decimal" value={dollars} onChange={(e) => setDollars(e.target.value)} placeholder="180.00" />
              </label>
            </>
          )}
          <label className="field">
            <span>Reason (optional, printed)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Job cancelled" />
          </label>
          <p className="tiny muted">
            {whole
              ? `Credits all ${money(inv.total_cents)}, GST included.`
              : cents > 0
                ? `Credits ${money(cents)} + GST.`
                : "Enter the amount to take off, before GST."}{" "}
            It's raised unsent — send it like an invoice, and then it reduces what's owed and goes to Xero.
          </p>
          {problem && (
            <p className="notice notice-crit" role="alert">
              {problem}
            </p>
          )}
          <div className="btn-row">
            <button
              className="btn btn-primary"
              type="button"
              disabled={credit.isPending || (!whole && (!description.trim() || !(cents > 0)))}
              onClick={() => credit.mutate()}
            >
              {credit.isPending ? "Raising…" : "Raise credit note"}
            </button>
            <button className="btn" type="button" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ----------------------------------------------------------------- payments */

const METHOD_LABEL: Record<string, string> = {
  eft: "Bank transfer",
  card: "Card",
  cash: "Cash",
  cheque: "Cheque",
  direct_debit: "Direct debit",
  other: "Other",
};

/**
 * Money received (BILL-2). An invoice in Xero is paid in Xero — the bank feed
 * matches it there — and Keystone reads that back hourly; one that isn't has
 * its payments recorded here. Never both, or it is counted twice.
 */
function PaymentsCard({
  inv,
  canRecord,
  onChanged,
}: {
  inv: Invoice;
  canRecord: boolean;
  onChanged: (next: Invoice) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dollars, setDollars] = useState("");
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<PaymentIn["method"]>("eft");
  const [reference, setReference] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const cents = Math.round(Number(dollars.replace(/[$,\s]/g, "")) * 100);

  const pay = useMutation({
    mutationFn: () => api.invoices.pay(inv.id, { amount_cents: cents, received_on: on, method, reference: reference.trim() || undefined }),
    onSuccess: (next) => {
      setOpen(false);
      setDollars("");
      setReference("");
      setProblem(null);
      onChanged(next);
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't record it."),
  });
  const undo = useMutation({
    mutationFn: (paymentId: string) => api.invoices.removePayment(inv.id, paymentId),
    onSuccess: onChanged,
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't remove it."),
  });

  return (
    <Card
      title="Payments"
      subtitle={inv.outstanding_cents === 0 ? "Nothing owing" : `${money(inv.outstanding_cents)} owing`}
    >
      {inv.payments.length === 0 && <p className="tiny muted">Nothing received yet.</p>}
      {inv.payments.map((p) => (
        <div key={p.id} className="tiny" style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "3px 0" }}>
          <span>
            <b className="mono">{p.amount_cents < 0 ? "−" : ""}{money(Math.abs(p.amount_cents))}</b>{" "}
            {p.received_at ? day(p.received_at) : ""} ·{" "}
            {p.source === "xero" ? (p.reference ?? "From Xero") : `${METHOD_LABEL[p.method] ?? p.method}${p.reference ? ` · ${p.reference}` : ""}`}
          </span>
          {canRecord && p.source === "keystone" && (
            <button
              className="linkish tiny"
              type="button"
              onClick={() => {
                if (window.confirm("Remove this payment? Use this for one recorded by mistake.")) undo.mutate(p.id);
              }}
            >
              Remove
            </button>
          )}
        </div>
      ))}

      {inv.payments_from_xero ? (
        <p className="tiny muted" style={{ marginTop: 8 }}>
          This invoice is in Xero, so payments are reconciled there against the bank feed. Keystone reads them back
          within the hour.
        </p>
      ) : (
        canRecord &&
        inv.outstanding_cents > 0 &&
        (open ? (
          <div className="stack" style={{ marginTop: 8 }}>
            <div className="form-grid">
              <label className="field">
                <span>Amount (inc GST)</span>
                <input inputMode="decimal" value={dollars} onChange={(e) => setDollars(e.target.value)} placeholder={(inv.outstanding_cents / 100).toFixed(2)} />
              </label>
              <label className="field">
                <span>Received</span>
                <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
              </label>
              <label className="field">
                <span>How</span>
                <select value={method} onChange={(e) => setMethod(e.target.value as PaymentIn["method"])}>
                  {Object.entries(METHOD_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Reference</span>
                <input value={reference} onChange={(e) => setReference(e.target.value)} />
              </label>
            </div>
            {problem && (
              <p className="notice notice-crit" role="alert">
                {problem}
              </p>
            )}
            <div className="btn-row">
              <button className="btn btn-primary" type="button" disabled={!(cents > 0) || pay.isPending} onClick={() => pay.mutate()}>
                {pay.isPending ? "Saving…" : "Record payment"}
              </button>
              <button className="btn" type="button" onClick={() => setDollars((inv.outstanding_cents / 100).toFixed(2))}>
                All of it
              </button>
              <button className="btn" type="button" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="btn" type="button" style={{ marginTop: 8 }} onClick={() => setOpen(true)}>
            Record a payment
          </button>
        ))
      )}
    </Card>
  );
}
